import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApiData, useAppDispatch } from 'reducers/hooks';
import { getSitesThunk, deleteSiteThunk, updateSiteThunk, postSiteThunk, duplicateSite } from 'reducers/fell/sites/action';
import { selectSites, selectStatus } from 'reducers/fell/sites/sitesSlice'
import { SiteType } from '../../lib/types';
import { ColumnDef, Row } from '@tanstack/react-table';
import { EditableTextCell } from 'components/Table/EditableTextCell'
import { DeleteRowAction } from 'components/Table/DeleteRowAction'
import { NavigateAction } from 'components/Table/NavigateAction'
import { DuplicateRowAction } from 'components/Table/DuplicateRowAction'
import { getProjectsThunk } from '../../reducers/fell/projects/action';
import { isNameLengthTooLong } from '../../lib/helpers';


type SitesData = {
    projectId,
    projectName,
    navigateBackToProjects,
    sitesData,
    handleAddSite: () => void,
    handleDeleteSiteRow: (rowId: number) => void,
    handleModalConfirmSiteDeletion: () => void,
    handleModalCancelSiteDeletion: () => void,
    handleDuplicateSiteRow: (siteId: number) => void,
    sitesTableColumns: ColumnDef<SiteType>[],
    showModal: boolean,
    maxNameLength: number,
    hasNameError: boolean,
    handleValidationMessageCloseEvent: () => void;
}

export const SitesInitialization = (): SitesData => {
    const dispatch = useAppDispatch();
    const params = useParams()
    const navigate = useNavigate()
    const [siteIdToDelete, setSiteIdToDelete] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const maxNameLength = 50;
    const [hasNameError, setHasNameError] = useState(false);

    const projectId = params.projectId
    const navigateBackToProjects = () => { navigate("/"); }
    const projectsData = useApiData(getProjectsThunk(), state => state.projects.data, state => state.projects.projectsStatus, navigate);
    const project = projectsData.find(project => project.id === parseInt(projectId))
    const projectName = project ? project.name : "";

    const [sitesData, setSitesData] = useState<SiteType[]>([]);
    const apiData = useApiData(getSitesThunk(projectId), selectSites(projectId), selectStatus(projectId), navigate);
    useEffect(() => {
        setSitesData(apiData);
    }, [apiData]);

    const handleAddSite = async () => {
        try {

            await dispatch(postSiteThunk({
                projectId: projectId
            }));

            await dispatch(getSitesThunk(
                projectId
            ));

        } catch (error) {
            console.error("Error adding site:", error);
            navigate('/error-page');
        }
    };

    const handleDeleteSiteRow = (siteId: number) => {
        if (siteId >= 0) {
            setSiteIdToDelete(siteId);
            setShowModal(true);
        }
    };

    const handleModalConfirmSiteDeletion = async () => {
        if (siteIdToDelete !== null) {            
            const siteToDelete = sitesData.find(site => site.id === siteIdToDelete);
            if (siteToDelete) {
                const siteId = siteToDelete.id.toString();
                try {
                    await dispatch(deleteSiteThunk({
                        projectId: projectId,
                        siteId: siteId
                    }))

                    await dispatch(getSitesThunk(
                        projectId
                    ));

                } catch (error) {
                    console.error("Error deleting site:", error);
                    navigate('/error-page');
                }
            }
        }

        setShowModal(false);
    };

    const handleModalCancelSiteDeletion = () => {
        setShowModal(false);
    };

    const handleDuplicateSiteRow = async (siteId: number) => {

        if (siteId >= 0) {
            const siteToDuplicate = sitesData.find(site => site.id === siteId);
            if (siteToDuplicate) {
                const checkNameTooLong = isNameLengthTooLong(siteToDuplicate.name, maxNameLength - 8)
                setHasNameError(checkNameTooLong)  // "Copy of " = 8 characters for duplicate site design name
                if (!checkNameTooLong) {
                    try {
                        await dispatch(duplicateSite({
                            siteId: siteToDuplicate.id,
                            projectId: projectId
                        }));

                        await dispatch(getSitesThunk(projectId));

                    } catch (error) {
                        console.error("Error duplicating site:", error);
                        navigate('/error-page');
                    }
                }
            }
        }
    };

    const handleUpdateSite = async (row: Row<any>, columnId: string, value: string) => {

        if (row) {
            const updatedSiteDataIndex = sitesData.findIndex(site => site.id === row.original.id);

            if (updatedSiteDataIndex >= 0) {
                const updatedSiteData = [...sitesData];
                updatedSiteData[updatedSiteDataIndex] = { ...updatedSiteData[updatedSiteDataIndex], [columnId]: value };

                if (updatedSiteData) {
                    // to be sure that the name of the site design after updating is still inferior than 50 characters
                    const currentSite = sitesData[updatedSiteDataIndex];
                    const updatedSite = updatedSiteData[updatedSiteDataIndex];
                    if (currentSite && updatedSite) {
                        if (currentSite.name !== updatedSite.name) { //checks if the name has been changed otherwise we don't post anything
                            const checkNameTooLong = isNameLengthTooLong(updatedSite.name, maxNameLength);
                            setHasNameError(checkNameTooLong)

                            if (!checkNameTooLong) {
                                setSitesData(updatedSiteData);

                                try {
                                    await dispatch(updateSiteThunk({
                                        site: updatedSite,
                                        projectId: projectId
                                    }));

                                    await dispatch(getSitesThunk(projectId));

                                } catch (error) {
                                    console.error("Error updating site:", error);
                                    navigate('/error-page');
                                }
                            }
                        }
                    }
                }
            }
        }
    };

    const handleValidationMessageCloseEvent = () => {
        setHasNameError(false)
    };

    const sitesTableColumns = [
        {
            accessorKey: 'primaryActions',
            header: '',
            cell: ({ row }) => (
                <div className="row-action-container">
                    <DuplicateRowAction row={row} duplicateClickHandler={handleDuplicateSiteRow} />
                    <NavigateAction rowIndex={row.index} url={`/project/${projectId}/site/${row.original.id}/sitedesign`} />
                </div>
            )
        },
        {
            accessorKey: 'name',
            header: 'Site',
            cell: ({ row, column }) => (
                <EditableTextCell
                    inputId={"xsd__e2e-sites-table-row-" + row.id + "-cell-input-" + column.id}
                    value={row.original.name}
                    row={row}
                    column={column}
                    updateCellData={handleUpdateSite}
                    maxLength={maxNameLength}
                />
            ),
        },
        {
            header: 'Date Created',
            accessorKey: 'formattedCreatedUtc',
        },
        //{
        //    header: "Modified",                       // TO BE ADDED LATER WHEN IT IS CORRECTLY HANLDED BY FELL
        //    accessorKey: "formattedUpdatedUtc"
        //}
        {
            accessorKey: 'secondaryActions',
            header: '',
            cell: ({ row }) => (
                <div className="row-action-container">
                    <DeleteRowAction row={row} deleteClickHandler={handleDeleteSiteRow} />
                </div>
            )
        },
    ]

    return {
        projectId,
        projectName,
        navigateBackToProjects,
        sitesData,
        handleAddSite,
        handleDeleteSiteRow,
        handleModalConfirmSiteDeletion,
        handleModalCancelSiteDeletion,
        handleDuplicateSiteRow,
        sitesTableColumns,
        showModal,
        maxNameLength,
        hasNameError,
        handleValidationMessageCloseEvent
    }
}
